import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './AdminDossiers.css';

const STORAGE_TOKEN_KEY = 'adminDossiersToken';
const LEGACY_TOKEN_KEYS = ['templateAdminToken', 'fjcAdminToken'];
const STORAGE_VIEWS_KEY = 'adminDossiersViews';
const STORAGE_ACTIVE_VIEW_KEY = 'adminDossiersActiveView';

const DEFAULT_VIEW_ID = 'default-view';

function readStoredJson(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (error) {
    return fallback;
  }
}

function persistJson(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // ignore storage errors
  }
}

function persistString(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // ignore storage errors
  }
}

function normalizeViews(rawViews, defaultFields) {
  if (!Array.isArray(rawViews) || rawViews.length === 0) {
    return [{ id: DEFAULT_VIEW_ID, name: 'Vue principale', fields: defaultFields }];
  }

  const normalized = rawViews
    .filter((view) => view && typeof view === 'object')
    .map((view, index) => {
      const id = view.id ? String(view.id) : `view-${index + 1}`;
      const name = view.name ? String(view.name) : `Vue ${index + 1}`;
      const fields = Array.isArray(view.fields)
        ? view.fields.map((field) => String(field)).filter(Boolean)
        : defaultFields;
      return { id, name, fields };
    });

  if (!normalized.find((view) => view.id === DEFAULT_VIEW_ID)) {
    normalized.unshift({ id: DEFAULT_VIEW_ID, name: 'Vue principale', fields: defaultFields });
  }

  return normalized;
}

function uniqueFields(list) {
  const seen = new Set();
  const values = [];
  (list || []).forEach((value) => {
    const key = String(value || '').trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    values.push(key);
  });
  return values;
}

function extFromName(name) {
  const safeName = String(name || '');
  const idx = safeName.lastIndexOf('.');
  if (idx < 0) return '';
  return safeName.slice(idx + 1).toLowerCase();
}

function isImageExt(ext) {
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
}

function isPdfExt(ext) {
  return ext === 'pdf';
}

export default function AdminDossiers() {
  const [token, setToken] = useState(() => {
    const current = window.localStorage.getItem(STORAGE_TOKEN_KEY);
    if (current) return current;
    for (const key of LEGACY_TOKEN_KEYS) {
      const value = window.localStorage.getItem(key);
      if (value) return value;
    }
    return '';
  });
  const [reviewLink, setReviewLink] = useState(null);
  const [reviewLinkBusy, setReviewLinkBusy] = useState(false);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [metadata, setMetadata] = useState(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedToken, setSelectedToken] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const [views, setViews] = useState(() => readStoredJson(STORAGE_VIEWS_KEY, []));
  const [activeViewId, setActiveViewId] = useState(() => window.localStorage.getItem(STORAGE_ACTIVE_VIEW_KEY) || DEFAULT_VIEW_ID);
  const [filters, setFilters] = useState({});

  const [docsState, setDocsState] = useState({
    open: false,
    token: '',
    files: [],
    index: 0,
    loading: false,
    error: ''
  });

  const adminFetch = useCallback(
    async (url, options = {}) => {
      const headers = {
        ...(options.headers || {}),
        ...(token ? { 'x-admin-token': token } : {})
      };
      return fetch(url, { ...options, headers });
    },
    [token]
  );

  // Un lien par vœu transmis : chaque responsable ne voit que sa filière.
  const createReviewLink = async (rowToken, voeu) => {
    setReviewLinkBusy(true); setReviewLink(null);
    try {
      const response = await adminFetch('/backend/salesforce/candidature/review/links', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: rowToken, ...(voeu ? { voeu } : {}) })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message || 'Impossible de créer le lien responsable.');
      setReviewLink({ url: new URL(payload.path, window.location.origin).href, expiresAt: payload.expiresAt });
      setStatus({ type: 'success', message: 'Lien responsable créé. Il donne accès à cette filière pendant 6 mois, tant que l’avis est attendu.' });
    } catch (error) { setStatus({ type: 'error', message: error.message }); }
    finally { setReviewLinkBusy(false); }
  };

  const availableFields = useMemo(() => metadata?.availableFields || [], [metadata]);
  const fieldByKey = useMemo(() => {
    const map = new Map();
    availableFields.forEach((field) => {
      map.set(field.key, field);
    });
    return map;
  }, [availableFields]);

  const activeView = useMemo(() => {
    const found = views.find((view) => view.id === activeViewId);
    return found || views[0] || null;
  }, [views, activeViewId]);

  const activeFields = useMemo(() => {
    if (!activeView) return [];
    const safe = uniqueFields(activeView.fields).filter((field) => fieldByKey.has(field));
    return safe;
  }, [activeView, fieldByKey]);

  const fieldsQueryParam = useMemo(() => activeFields.join(','), [activeFields]);

  useEffect(() => {
    persistString(STORAGE_TOKEN_KEY, token);
    LEGACY_TOKEN_KEYS.forEach((key) => persistString(key, token));
  }, [token]);

  useEffect(() => {
    persistJson(STORAGE_VIEWS_KEY, views);
  }, [views]);

  useEffect(() => {
    persistString(STORAGE_ACTIVE_VIEW_KEY, activeViewId);
  }, [activeViewId]);

  const loadMetadata = useCallback(
    async (forceRefresh = false) => {
      setStatus({ type: 'loading', message: 'Chargement des metadonnees...' });
      try {
        const url = `/backend/api/admin/dossiers/metadata${forceRefresh ? '?refresh=1' : ''}`;
        const res = await adminFetch(url);
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          if (res.status === 404) {
            throw new Error('Endpoint admin introuvable. Redemarrez le backend.');
          }
          if (res.status === 401) {
            throw new Error('Acces refuse: token admin invalide.');
          }
          throw new Error(data.message || 'Acces refuse.');
        }

        setMetadata(data);

        const normalizedViews = normalizeViews(views, data.defaultVisibleFields || []);
        const filteredViews = normalizedViews.map((view) => ({
          ...view,
          fields: uniqueFields(view.fields).filter((field) => (data.availableFields || []).some((entry) => entry.key === field))
        }));

        const repairedViews = filteredViews.map((view) => {
          if (view.fields.length > 0) return view;
          if (view.id === DEFAULT_VIEW_ID) {
            return { ...view, fields: data.defaultVisibleFields || [] };
          }
          return view;
        });

        setViews(repairedViews);
        if (!repairedViews.find((view) => view.id === activeViewId)) {
          setActiveViewId(repairedViews[0]?.id || DEFAULT_VIEW_ID);
        }

        const defaultFilters = {};
        (data.filterDefinitions || []).forEach((definition) => {
          defaultFilters[definition.key] = '';
        });
        setFilters((prev) => ({ ...defaultFilters, ...prev }));

        setStatus({ type: 'success', message: `${data.total || 0} dossiers V2 indexes.` });
      } catch (error) {
        setStatus({ type: 'error', message: error.message || 'Impossible de charger les metadonnees.' });
      }
    },
    [adminFetch, views, activeViewId]
  );

  useEffect(() => {
    loadMetadata(false);
  }, []);

  useEffect(() => {
    if (!metadata) return;

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setIsLoadingSuggestions(true);
      try {
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('limit', '10');
        const res = await adminFetch(`/backend/api/admin/dossiers/search?${params.toString()}`, {
          signal: controller.signal
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Erreur de recherche.');
        }
        setSuggestions(Array.isArray(data.items) ? data.items : []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setSuggestions([]);
        }
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query, metadata, adminFetch]);

  useEffect(() => {
    if (!metadata) return;

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setIsLoadingRows(true);
      try {
        const params = new URLSearchParams();
        if (query) params.set('q', query);
        if (selectedToken) params.set('selectedToken', selectedToken);
        params.set('limit', selectedToken ? '1' : '300');
        if (fieldsQueryParam) {
          params.set('fields', fieldsQueryParam);
        }

        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.set(key, value);
        });

        const res = await adminFetch(`/backend/api/admin/dossiers?${params.toString()}`, {
          signal: controller.signal
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Erreur de chargement.');
        }

        setRows(Array.isArray(data.rows) ? data.rows : []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setRows([]);
        }
      } finally {
        setIsLoadingRows(false);
      }
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [metadata, query, selectedToken, filters, fieldsQueryParam, adminFetch]);

  const toggleField = (fieldKey) => {
    if (!activeView) return;

    setViews((prev) =>
      prev.map((view) => {
        if (view.id !== activeView.id) return view;
        const exists = view.fields.includes(fieldKey);
        const nextFields = exists ? view.fields.filter((field) => field !== fieldKey) : [...view.fields, fieldKey];
        return { ...view, fields: nextFields };
      })
    );
  };

  const createView = () => {
    const nextId = `view-${Date.now()}`;
    const nextName = `Vue ${views.length + 1}`;
    const nextView = {
      id: nextId,
      name: nextName,
      fields: activeFields.length > 0 ? activeFields : metadata?.defaultVisibleFields || []
    };
    setViews((prev) => [...prev, nextView]);
    setActiveViewId(nextId);
  };

  const renameActiveView = () => {
    if (!activeView || activeView.id === DEFAULT_VIEW_ID) return;
    const nextName = window.prompt('Nouveau nom de vue', activeView.name || '');
    if (!nextName || !nextName.trim()) return;
    const cleanName = nextName.trim();
    setViews((prev) => prev.map((view) => (view.id === activeView.id ? { ...view, name: cleanName } : view)));
  };

  const deleteActiveView = () => {
    if (!activeView || activeView.id === DEFAULT_VIEW_ID) return;
    const confirmed = window.confirm(`Supprimer la vue \"${activeView.name}\" ?`);
    if (!confirmed) return;
    setViews((prev) => {
      const next = prev.filter((view) => view.id !== activeView.id);
      setActiveViewId(DEFAULT_VIEW_ID);
      return next;
    });
  };

  const selectSuggestion = (item) => {
    setSelectedToken(item.token || '');
    const label = [item.prenom, item.nom].filter(Boolean).join(' ');
    if (label) {
      setQuery(label);
    }
    setSuggestions([]);
  };

  const clearSelection = () => {
    setSelectedToken('');
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const openDocuments = async (rowToken) => {
    setDocsState({ open: true, token: rowToken, files: [], index: 0, loading: true, error: '' });
    try {
      const res = await adminFetch(`/backend/api/admin/dossiers/${encodeURIComponent(rowToken)}/documents`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Impossible de charger les documents.');
      }

      setDocsState({
        open: true,
        token: rowToken,
        files: Array.isArray(data.files) ? data.files : [],
        index: 0,
        loading: false,
        error: ''
      });
    } catch (error) {
      setDocsState({ open: true, token: rowToken, files: [], index: 0, loading: false, error: error.message || 'Erreur.' });
    }
  };

  const closeDocuments = () => {
    setDocsState({ open: false, token: '', files: [], index: 0, loading: false, error: '' });
  };

  const stepDocument = (delta) => {
    setDocsState((prev) => {
      if (!prev.files.length) return prev;
      const nextIndex = (prev.index + delta + prev.files.length) % prev.files.length;
      return { ...prev, index: nextIndex };
    });
  };

  const downloadAll = async (rowToken) => {
    try {
      const res = await adminFetch(`/backend/api/admin/dossiers/${encodeURIComponent(rowToken)}/documents.zip`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Telechargement impossible.');
      }

      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `documents_${rowToken}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      window.alert(error.message || 'Erreur pendant le telechargement.');
    }
  };

  const currentDoc = docsState.files[docsState.index] || null;
  const currentDocExt = extFromName(currentDoc?.name);

  return (
    <div className="admin-dossiers">
      <header className="admin-dossiers__header">
        <div>
          <p className="admin-dossiers__kicker">Administration</p>
          <h1>Pilotage des dossiers V2</h1>
          <a href="/admin/mails">E-mails automatiques</a>
          <p className="admin-dossiers__subtitle">
            Recherche dynamique, colonnes configurables, filtres modifiables et acces direct aux pieces d&apos;un dossier.
          </p>
        </div>
        <div className="admin-dossiers__auth">
          <label>
            Token admin
            <input value={token} onChange={(event) => setToken(event.target.value)} placeholder="x-admin-token" />
          </label>
          <button type="button" onClick={() => loadMetadata(true)}>
            Recharger
          </button>
        </div>
      </header>

      {status.type !== 'idle' && <div className={`admin-dossiers__status admin-dossiers__status--${status.type}`}>{status.message}</div>}

      {reviewLink && <section className="admin-dossiers__search-panel">
        <label>Lien réservé au responsable
          <input readOnly value={reviewLink.url} onFocus={event => event.target.select()} />
        </label>
        <a href={reviewLink.url} target="_blank" rel="noreferrer">Ouvrir le dossier en lecture seule</a>
        <p>Valable jusqu’au {new Date(reviewLink.expiresAt).toLocaleDateString('fr-FR')}. À transmettre uniquement au responsable.</p>
      </section>}

      <section className="admin-dossiers__search-panel">
        <label className="admin-dossiers__search-label">
          Recherche dossier (nom, prenom, mail)
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelectedToken('');
            }}
            placeholder="Ex: DUPONT, marie, mail@domaine.fr"
          />
        </label>

        {query.trim() && (
          <div className="admin-dossiers__suggestions">
            <div className="admin-dossiers__suggestions-title">
              Suggestions {isLoadingSuggestions ? '(chargement...)' : ''}
            </div>
            {suggestions.length === 0 ? (
              <div className="admin-dossiers__suggestion-empty">Aucune suggestion.</div>
            ) : (
              suggestions.map((item) => (
                <button key={item.token} type="button" className="admin-dossiers__suggestion-item" onClick={() => selectSuggestion(item)}>
                  <strong>{[item.prenom, item.nom].filter(Boolean).join(' ') || 'Sans identite'}</strong>
                  <span>{item.mail || 'Sans mail'}</span>
                  <span>{item.niveau || '-'} / {item.filiere || '-'}</span>
                </button>
              ))
            )}
          </div>
        )}

        {selectedToken && (
          <div className="admin-dossiers__selected">
            Dossier selectionne: <code>{selectedToken}</code>
            <button type="button" onClick={clearSelection}>Retour a la liste</button>
          </div>
        )}
      </section>

      <section className="admin-dossiers__toolbar">
        <div className="admin-dossiers__views">
          <label>
            Liste de vue
            <select value={activeViewId} onChange={(event) => setActiveViewId(event.target.value)}>
              {views.map((view) => (
                <option key={view.id} value={view.id}>
                  {view.name}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={createView}>Nouvelle vue</button>
          <button type="button" onClick={renameActiveView} disabled={!activeView || activeView.id === DEFAULT_VIEW_ID}>
            Renommer
          </button>
          <button type="button" onClick={deleteActiveView} disabled={!activeView || activeView.id === DEFAULT_VIEW_ID}>
            Supprimer
          </button>
        </div>

        <div className="admin-dossiers__filters">
          {(metadata?.filterDefinitions || []).map((definition) => (
            <label key={definition.key}>
              {definition.label}
              <select value={filters[definition.key] || ''} onChange={(event) => updateFilter(definition.key, event.target.value)}>
                <option value="">Tous</option>
                {(definition.options || []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      </section>

      <section className="admin-dossiers__field-picker">
        <h2>Champs visibles</h2>
        <div className="admin-dossiers__field-grid">
          {availableFields.map((field) => {
            const checked = activeFields.includes(field.key);
            return (
              <label key={field.key} className={checked ? 'is-active' : ''}>
                <input type="checkbox" checked={checked} onChange={() => toggleField(field.key)} />
                <span>{field.label}</span>
              </label>
            );
          })}
        </div>
      </section>

      <section className="admin-dossiers__rows">
        <div className="admin-dossiers__rows-header">
          <h2>Liste dossiers {isLoadingRows ? '(chargement...)' : ''}</h2>
          <span>{rows.length} ligne(s)</span>
        </div>

        {rows.map((row) => (
          <article key={row.token} className="admin-row">
            <div className="admin-row__cells">
              {activeFields.map((fieldKey) => (
                <div key={fieldKey} className="admin-row__cell">
                  <div className="admin-row__label">{fieldByKey.get(fieldKey)?.label || fieldKey}</div>
                  <div className="admin-row__value">{row.values?.[fieldKey] || '-'}</div>
                </div>
              ))}
            </div>

            <div className="admin-row__actions">
              <a href={`/?tkn=${encodeURIComponent(row.token)}`} target="_blank" rel="noreferrer">
                Voir dossier
              </a>
              {row.configuration === 'Candidature' && (row.filieresTransmises || []).map((item, index) => (
                <button key={`${item.voeu || 'v'}-${index}`} type="button" disabled={reviewLinkBusy} onClick={() => createReviewLink(row.token, item.voeu)}>
                  {row.filieresTransmises.length > 1 ? `Lien responsable : ${item.nom}` : 'Créer le lien responsable'}
                </button>
              ))}
              <button type="button" onClick={() => openDocuments(row.token)}>Docs</button>
              <button type="button" onClick={() => downloadAll(row.token)}>Tout telecharger</button>
            </div>
          </article>
        ))}

        {rows.length === 0 && !isLoadingRows && <div className="admin-dossiers__empty">Aucun dossier a afficher.</div>}
      </section>

      {docsState.open && (
        <aside className="admin-docs">
          <div className="admin-docs__header">
            <div>
              <h3>Documents</h3>
              <p>{docsState.token}</p>
            </div>
            <button type="button" onClick={closeDocuments}>Fermer</button>
          </div>

          {docsState.loading && <div className="admin-docs__state">Chargement...</div>}
          {docsState.error && <div className="admin-docs__state admin-docs__state--error">{docsState.error}</div>}
          {!docsState.loading && !docsState.error && docsState.files.length === 0 && (
            <div className="admin-docs__state">Aucun document disponible.</div>
          )}

          {!docsState.loading && !docsState.error && docsState.files.length > 0 && currentDoc && (
            <>
              <div className="admin-docs__controls">
                <button type="button" onClick={() => stepDocument(-1)}>Precedent</button>
                <span>{docsState.index + 1} / {docsState.files.length}</span>
                <button type="button" onClick={() => stepDocument(1)}>Suivant</button>
              </div>

              <div className="admin-docs__filename">{currentDoc.name}</div>

              <div className="admin-docs__viewer">
                {isPdfExt(currentDocExt) && <iframe title={currentDoc.name} src={currentDoc.url} />}
                {isImageExt(currentDocExt) && <img src={currentDoc.url} alt={currentDoc.name} />}
                {!isPdfExt(currentDocExt) && !isImageExt(currentDocExt) && (
                  <a href={currentDoc.url} target="_blank" rel="noreferrer">
                    Ouvrir ce fichier
                  </a>
                )}
              </div>
            </>
          )}
        </aside>
      )}
    </div>
  );
}
